import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../../../../../redux/store';
import { createCollectionActivity } from '../../../../../../redux/services/collectionActivityService';
import { getSupplierSummary } from '../../../../../../redux/services/suppliersService';
import { DillModal } from '../../../../../general/Dill/DillModal';
import { DillButton } from '../../../../../general/Dill/DillButton';
import { AppInputField } from '../../../../../general/AppInputField/AppInputField';
import { AppDropDown } from '../../../../../general/AppDropDown/AppDropDown';
import AppDatePicker from '../../../../../general/AppDatePicker/AppDatePicker';
import { openMessageModal } from '../../../../../../redux/globalSlices/genericSlice';
import {
  BuyerContact,
  TaskPriority,
  TaskStatus,
  TaskType,
  CollectionActivityReference,
} from '@dill/dill-shared';
import LexicalEditor from '../../../../../general/LexicalEditor';
import ActivityReferenceSelector from '../../ActivityReferenceSelector/ActivityReferenceSelector';

interface AddTaskModalProps {
  open: boolean;
  buyerId: string;
  onClose: () => void;
  onSuccess?: () => void;
  initialReferences?: CollectionActivityReference[];
  contacts?: BuyerContact[];
}

const priorityOptions: { value: TaskPriority; label: string; color: string }[] = [
  { value: 'HIGH', label: 'High', color: 'text-red-600' },
  { value: 'MEDIUM', label: 'Medium', color: 'text-yellow-600' },
  { value: 'LOW', label: 'Low', color: 'text-green-600' },
];

const taskTypeOptions: { value: TaskType; label: string }[] = [
  { value: 'TO_DO', label: 'To-Do' },
  { value: 'CALL', label: 'Call' },
  { value: 'EMAIL', label: 'Email' },
  { value: 'TEXT_MESSAGE', label: 'Text' },
];

function AddTaskModal({
  open,
  buyerId,
  onClose,
  onSuccess,
  initialReferences,
  contacts,
}: AddTaskModalProps) {
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);
  const currentSupplier = useAppSelector((state) => state.auth.currentSupplier);
  const { loadingList } = useAppSelector((state) => state.collectionActivities);
  const { supplierUsers } = useAppSelector((state) => state.supplier);

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState<string | undefined>(undefined);
  const [dueDate, setDueDate] = useState<Date | null>(null);
  const [priority, setPriority] = useState<TaskPriority>('MEDIUM');
  const [taskType, setTaskType] = useState<TaskType>('TO_DO');
  const [assignedTo, setAssignedTo] = useState<string>('');
  const [references, setReferences] = useState<CollectionActivityReference[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [titleError, setTitleError] = useState('');

  const isLoading = loadingList.includes('createCollectionActivity');

  const handleDescriptionChange = useCallback((content: string) => {
    setDescription(content || undefined);
  }, []);

  useEffect(() => {
    if (open) {
      if (initialReferences) {
        setReferences(initialReferences);
      }
      setAssignedTo(user?.id || '');
      if (currentSupplier?.id && supplierUsers.length === 0) {
        dispatch(getSupplierSummary({ supplierId: currentSupplier.id }));
      }
    }
  }, [open, initialReferences, user?.id, currentSupplier?.id, supplierUsers.length, dispatch]);

  const handleSubmit = async () => {
    if (!title.trim()) {
      setTitleError('Please enter a task title');
      return;
    }

    if (!currentSupplier?.id) {
      return;
    }

    if (!user?.id) {
      return;
    }

    setIsSubmitting(true);

    try {
      const result = await dispatch(
        createCollectionActivity({
          supplierId: currentSupplier.id,
          activityData: {
            supplierId: currentSupplier.id,
            buyerId,
            activityType: 'TASK',
            source: 'MANUAL',
            performedBy: user.id,
            date: new Date(),
            taskDetails: {
              title,
              description: description ? JSON.parse(description) : undefined,
              dueDate: dueDate || undefined,
              status: 'PENDING' as TaskStatus,
              priority,
              taskType,
              assignedTo: assignedTo || user.id,
            },
            references: references.length > 0 ? references : undefined,
          },
        })
      );

      if (result.payload && (result.payload as any).success) {
        setTitle('');
        setDescription(undefined);
        setDueDate(null);
        setPriority('MEDIUM');
        setReferences([]);
        onSuccess?.();
        onClose();
      }
    } catch (error) {
      console.error('Failed to create task:', error);
      dispatch(
        openMessageModal({
          title: 'Error',
          subTitle: 'Failed to create task. Please try again.',
          buttonText: 'OK',
          modalType: 'ERROR',
        })
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // Build assigned-to options from supplier users
  const assignedToOptions = useMemo(() => {
    const options: { value: string; label: string }[] = [];
    for (const u of supplierUsers) {
      options.push({
        value: u.id,
        label: `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email,
      });
    }
    return options;
  }, [supplierUsers]);

  const handleClose = () => {
    if (!isLoading) {
      setTitle('');
      setDescription(undefined);
      setDueDate(null);
      setPriority('MEDIUM');
      setTaskType('TO_DO');
      setReferences([]);
      setTitleError('');
      onClose();
    }
  };

  return (
    <DillModal
      open={open}
      onClose={handleClose}
      title="Create Task"
      maxWidth="576px"
      footer={
        <>
          <DillButton
            onClick={handleClose}
            variant="secondary"
            isDisabled={isLoading}
            label="Cancel"
          />
          <DillButton
            onClick={handleSubmit}
            variant="primary"
            isLoading={isLoading}
            label="Create Task"
          />
        </>
      }>
      <AppInputField
        label="Task Title"
        id="title"
        value={title}
        onTextChange={(val) => {
          setTitle(val);
          if (titleError) setTitleError('');
        }}
        placeholder="Enter task title..."
        isDisabled={isLoading}
        isRequired
        errorText={titleError}
      />

      <LexicalEditor
        label="Description (Optional)"
        onChange={handleDescriptionChange}
        placeholder="Enter task description..."
        editable={!isLoading}
        minHeight="80px"
      />

      <div className="grid grid-cols-2 gap-4">
        <AppDatePicker
          label="Due Date (Optional)"
          selectedDate={dueDate}
          onDateSelected={(date) => setDueDate(date ?? null)}
          isDisabled={isLoading}
        />
        <AppDropDown
          label="Priority"
          items={priorityOptions}
          value={priority}
          onSelectChange={(selected) => setPriority(selected?.value as TaskPriority)}
          isDisabled={isLoading}
          placeholder="Select priority"
        />
      </div>

      <AppDropDown
        label="Task Type"
        items={taskTypeOptions}
        value={taskType}
        onSelectChange={(selected) => setTaskType(selected?.value as TaskType)}
        isDisabled={isLoading}
        placeholder="Select task type"
      />

      <AppDropDown
        label="Assigned To"
        items={assignedToOptions}
        value={assignedTo}
        onSelectChange={(selected) => setAssignedTo(selected?.value || '')}
        isDisabled={isLoading}
        placeholder="Select team member"
      />

      <ActivityReferenceSelector
        label="References (Optional)"
        buyerId={buyerId}
        value={references}
        onChange={setReferences}
        isDisabled={isLoading}
        contacts={contacts}
      />
    </DillModal>
  );
}

export default AddTaskModal;
